import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Store, Package, Users, BarChart3, LogOut } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { formatINR } from "../../lib/utils.fmt";
import { toast } from "sonner";
import PhoneOtpLogin from "../PhoneOtpLogin";
import { getTenantSlug } from "../../lib/tenant";
import { LoadingState } from "../../components/EmptyState";

export function SellerLogin() {
  const isDemoTenant = getTenantSlug() === "dairyfresh";
  return (
    <PhoneOtpLogin
      role="seller"
      title="Seller Portal"
      subtitle="Marketplace Vendor"
      icon={Store}
      accentClass="bg-brand-leaf"
      buttonClass="bg-brand-leaf hover:bg-green-700"
      channelActiveClass="border-brand-leaf bg-brand-leaf/10 text-brand-leaf"
      defaultPhone={isDemoTenant ? "+919999900003" : ""}
      tenantSlug={getTenantSlug()}
      redirectTo="/seller/dashboard"
      testIdPrefix="seller-login"
    />
  );
}

export function SellerDashboard() {
  const { user, tenant, logout, loading } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "seller")) nav("/seller/login");
  }, [user, loading]);

  useEffect(() => {
    // Sellers see catalog (tenant-wide) — using public endpoint for demo
    if (user?.role === "seller" && tenant?.slug) {
      api.get(`/api/public/tenant/${tenant.slug}/products`).then(({ data }) => setProducts(data.products))
        .catch((e) => toast.error(friendlyMessage(e)));
    }
  }, [user, tenant]);

  if (loading || !user) return <LoadingState fullHeight className="min-h-screen" />;

  return (
    <div className="min-h-screen bg-brand-cream page-enter" data-testid="seller-dashboard-page">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-leaf flex items-center justify-center">
              <Store className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-extrabold tracking-tight">{user.name}</div>
              <div className="text-xs text-neutral-500">Seller · {tenant?.name}</div>
            </div>
          </div>
          <button onClick={logout} className="px-4 h-10 rounded-2xl bg-brand-cream border border-neutral-200 font-bold text-sm inline-flex items-center gap-1.5" data-testid="seller-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-brand-leaf">Marketplace Addon</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Seller Dashboard</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Products Listed" value={products.length} />
          <Kpi label="Orders (30d)" value="—" />
          <Kpi label="Payouts (30d)" value={formatINR(0)} />
          <Kpi label="Pending Payout" value={formatINR(0)} />
        </div>
        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <h3 className="font-heading font-bold text-lg mb-4">My Catalog</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {products.slice(0, 6).map((p) => (
              <div key={p.id} className="border border-neutral-200 rounded-2xl overflow-hidden" data-testid={`seller-product-${p.id}`}>
                <div className="aspect-video bg-neutral-100">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-xs uppercase tracking-wider font-bold text-brand-leaf">{p.category}</div>
                  <div className="font-semibold text-sm mt-1">{p.name}</div>
                  <div className="font-mono font-bold mt-1">{formatINR(p.price)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-brand-leaf/10 border border-brand-leaf/30 p-6">
          <div className="font-heading font-bold text-lg">Seller payout details (coming Phase 2)</div>
          <p className="text-sm text-neutral-700 mt-1">
            Multi-seller marketplace is enabled by the Super Admin per tenant. Once activated, you'll be able to manage your own products, view per-seller orders, and trigger weekly payouts.
          </p>
        </div>
      </main>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white rounded-3xl border border-neutral-200 p-5">
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}
